import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FlipPage from 'react-flip-page';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const Catalog = () => {
  const { t, i18n } = useTranslation();
  const [numPages, setNumPages] = useState(null);
  const [pdfPages, setPdfPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState(null);
  const [pdfLanguage, setPdfLanguage] = useState(() => {
    // Initialize with current app language
    return i18n.language || 'tr';
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const flipRef = useRef(null);
  const firstRender = useRef(true);

  console.log('Rendering Catalog component');

  // Choose the PDF based on the current PDF language
  const getPdfPath = () => {
    const path = (() => {
      switch (pdfLanguage) {
        case 'en':
        case 'ru':
        case 'ar':
          return '/catalogs/orbitras-katalog-en.pdf';
        case 'tr':
        default:
          return '/catalogs/orbitras-katalog-tr.pdf';
      }
    })();
    console.log(`Selected PDF path: ${path} for language: ${pdfLanguage}`);
    return path;
  };

  // No need for toggle function anymore as we're only using the app's locale
  const pdfPath = getPdfPath();

  // Sync with app language changes
  useEffect(() => {
    if (i18n.language !== pdfLanguage) {
      console.log(`Language changed from ${pdfLanguage} to ${i18n.language}, updating PDF language`);
      setPdfLanguage(i18n.language);
    }
  }, [i18n.language, pdfLanguage]);

  // Memoized callbacks for page navigation to avoid re-renders
  const handleNextPage = useCallback(() => {
    if (flipRef.current) {
      flipRef.current.gotoNextPage();
    }
  }, []);

  const handlePrevPage = useCallback(() => {
    if (flipRef.current) {
      flipRef.current.gotoPreviousPage();
    }
  }, []);

  // Safe page change handler
  const handlePageChange = useCallback((page) => {
    console.log('Page changed to:', page);
    setCurrentPage(page);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset state when PDF language changes
  useEffect(() => {
    console.log('PDF language changed to:', pdfLanguage, '- resetting PDF state');
    setNumPages(null);
    setPdfPages([]);
    setLoading(true);
    setLoadingProgress(0);
    setError(null);
    setCurrentPage(0);
  }, [pdfLanguage]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Attempt to load the PDF and pre-render the pages
  useEffect(() => {
    let isMounted = true;
    
    const loadPdf = async () => {
      if (!isMounted) return;
      
      try {
        console.log('Loading PDF:', pdfPath);
        setLoadingProgress(5);
        
        // Use the react-pdf pdfjs object which is already configured
        const pdf = await pdfjs.getDocument(pdfPath).promise;
        console.log('PDF loaded with', pdf.numPages, 'pages');
        
        if (!isMounted) return;
        setNumPages(pdf.numPages);
        
        const images = [];
        const totalPages = pdf.numPages;
        
        for (let i = 1; i <= totalPages; i++) {
          if (!isMounted) return;
          
          console.log('Rendering page', i, 'of', totalPages);
          setLoadingProgress(Math.floor((i / totalPages) * 90) + 5);
          
          try {
            // Get the page
            const page = await pdf.getPage(i);
            
            // Set the scale for rendering
            const scale = 1.5;
            const viewport = page.getViewport({ scale });
            
            // Create a canvas for rendering
            const canvas = document.createElement('canvas');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            // Get canvas context and render the page
            const context = canvas.getContext('2d');
            const renderContext = {
              canvasContext: context,
              viewport: viewport
            };
            
            await page.render(renderContext).promise;
            
            // Convert canvas to image
            const image = canvas.toDataURL('image/jpeg', 0.9);
            images.push(image);
            
            if (i === totalPages && isMounted) {
              console.log('All pages rendered successfully');
              setPdfPages(images);
              setLoading(false);
              setLoadingProgress(100);
            }
          } catch (pageError) {
            console.error('Error rendering page', i, pageError);
            if (isMounted) {
              setError(t('catalog.error'));
              setLoading(false);
            }
            break;
          }
        }
      } catch (pdfError) {
        console.error('Error loading PDF:', pdfError);
        if (isMounted) {
          setError(t('catalog.error'));
          setLoading(false);
        }
      }
    };
    
    if (pdfPages.length === 0) {
      loadPdf();
    }
    
    return () => {
      isMounted = false;
    };
  }, [pdfPath, t, pdfPages.length]);

  // Calculate dimensions based on viewport
  const getPageWidth = useCallback(() => {
    // Adjust page width for mobile screens
    if (windowWidth < 768) {
      return Math.min(windowWidth * 0.4, 250);
    }
    return Math.min(windowWidth * 0.45, 500);
  }, [windowWidth]);

  const getPageHeight = useCallback(() => {
    // Adjust page height for mobile screens
    if (windowWidth < 768) {
      return Math.min(windowHeight * 0.4, 400);
    }
    return Math.min(windowHeight * 0.7, 700);
  }, [windowHeight, windowWidth]);

  // Loading placeholder
  const LoadingPlaceholder = () => (
    <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center py-10 md:py-20">
      <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-primaryBlue border-t-transparent rounded-full animate-spin mb-6"></div>
      <p className="text-lg text-gray-600 mb-4">{t('catalog.loading')}</p>
      <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 mb-2">
        <div className="bg-primaryBlue h-2.5 rounded-full" style={{ width: `${loadingProgress}%` }}></div>
      </div>
      <p className="text-sm text-gray-500 mb-6">{loadingProgress}% - {t('catalog.preparingPages')}</p>
    </div>
  );

  // Book page component with memoization to prevent unnecessary re-renders
  const FlipBookViewer = React.memo(() => {
    const pageWidth = getPageWidth();
    const pageHeight = getPageHeight();
    
    if (pdfPages.length === 0) {
      return <LoadingPlaceholder />;
    }

    // Only create the pages once - no cover pages, just PDF pages
    const bookPages = pdfPages.map((imgSrc, index) => (
      <div 
        key={`flip-page-${index}`} 
        className="h-full w-full relative"
      >
        <img 
          src={imgSrc} 
          alt={`Page ${index + 1}`} 
          className="h-full w-full object-contain"
        />
        <div className="absolute bottom-2 right-2 bg-white/70 px-2 py-1 rounded-md text-xs">
          {index + 1} / {pdfPages.length}
        </div>
      </div>
    ));
    
    return (
      <div className="w-full flex flex-col items-center">
        {/* Navigation controls - improved for mobile */}
        <div className="w-full flex flex-col md:flex-row justify-center items-center gap-4 mb-6">
          <div className="flex items-center justify-center space-x-4">
            <button 
              onClick={handlePrevPage}
              className="p-3 rounded-full bg-primaryBlue text-white hover:bg-primaryBlue/80 transition-colors"
              aria-label="Previous page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              onClick={handleNextPage}
              className="p-3 rounded-full bg-primaryBlue text-white hover:bg-primaryBlue/80 transition-colors"
              aria-label="Next page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <p className="text-gray-600 text-center px-4 text-sm md:text-base">
            {t('catalog.instructions')}
          </p>
        </div>
        
        <div 
          className="bg-white shadow-xl rounded-lg p-2 mb-8 mx-auto w-full"
          style={{ 
            maxWidth: Math.min(pageWidth * 2 + 20, windowWidth - 32), // Adjust for small screens
            height: pageHeight + 20,
            overflow: 'hidden'
          }}
        >
          <FlipPage 
            orientation="horizontal"
            uncutPages={true}
            animationDuration={300}
            showSwipeHint={false}
            pageBackground="#fff"
            width={Math.min(pageWidth, (windowWidth - 64) / 2)} // Adjust width for small screens
            height={pageHeight}
            ref={flipRef}
            className="mx-auto"
            onPageChange={handlePageChange}
            disableSwipe={windowWidth < 500} // Disable swipe on very small screens to prevent issues
            responsive={true}
            startAt={currentPage}
            threshold={10}
            maxAngle={25}
          >
            {bookPages}
          </FlipPage>
        </div>
        
        <div className="text-center mb-8">
          <p className="text-gray-600 mb-4">
            {t('catalog.pageInfo', { current: currentPage + 1, total: pdfPages.length })}
          </p>
          <a 
            href={pdfPath} 
            download
            className="inline-flex items-center px-6 py-3 bg-primaryBlue text-white rounded-md hover:bg-primaryBlue/80 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {t('catalog.download')}
          </a>
        </div>
      </div>
    );
  });
  
  // Assign a display name for the memoized component
  FlipBookViewer.displayName = 'FlipBookViewer';

  // For debugging
  useEffect(() => {
    console.log('Current page state:', currentPage);
  }, [currentPage]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative h-[40vh] bg-primaryNavy">
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <div className="max-w-4xl mx-auto px-4">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {t('catalog.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-white/90">
              {t('catalog.hero.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Catalog Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {error ? (
          <div className="text-center py-10 md:py-20">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-lg text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-primaryBlue text-white rounded-md hover:bg-primaryBlue/80 transition-colors shadow-md"
            >
              {t('catalog.retry')}
            </button>
          </div>
        ) : loading && pdfPages.length === 0 ? (
          <LoadingPlaceholder />
        ) : (
          <FlipBookViewer />
        )}
        
        {/* Small language indicator badge */}
        <div className="text-center mt-4 mb-8">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {pdfLanguage === 'tr' ? 'Türkçe' : 'English'} PDF
          </span>
        </div>
      </div>
    </div>
  );
};

export default Catalog; 